import React from 'react';
import * as T from '../time.js';
import { api } from '../api.js';
import { mapTasks } from '../mapping.js';
import { Empty, Icon, Lbl, Loading, Modal, PH, PageTabs, Panel, StatusPill } from './components.jsx';
import { Button, DataBar, DataTable, Field, Metric, Pill, SegmentedControl } from './ds/fgcp-ds.jsx';
import { PeoplePicker } from './people-picker.jsx';
import { Attachments } from './attachments.jsx';
import { useTasks } from './use-tasks.js';

// task.jsx — 업무·요청(계획 문서 5.2 2-5). 볼 수 있는 업무·탭(범위)·할 수 있는 일(내용 고치기·진행 바꾸기·지우기)은 서버가 정한다(scopes·actions).
// 등록자는 내용을, 담당자는 진행(상태·진행률)을 바꾼다. 상세는 #/task/<id>(알림·검색 링크)로도 열린다.
// 2-5b: 댓글·첨부(저장 때 fileIds)·참조(결재·게시글 — 통합 검색에서 고른다, 볼 수 없는 대상은 서버가 제목을 숨긴다)·링크(https)·칸반(상태 열, 옮기기 단추).

const { useState: uS, useEffect: uE, useCallback: uCb } = React;
const SCOPE_LABEL = { assigned: '내 담당', requested: '내가 요청', watching: '참조', department: '부서', all: '전체' };
const STATUS = [['todo', '등록'], ['doing', '진행'], ['hold', '보류'], ['done', '완료']];
const PRIO = { high: ['높음', 'crit'], mid: ['보통', 'warn'], low: ['낮음', 'neutral'] };
const box = { padding: '7px 9px', border: '1px solid var(--line-strong)', borderRadius: 'var(--r-ctl)', background: 'var(--surface)', color: 'var(--ink)', fontSize: 12.5, boxSizing: 'border-box' };
const ymd = iso => (iso ? T.ymd(iso) : '');
/** 기한 날짜(브라우저 달력) → 그날 끝(23:59) — 그날 안에 끝내면 지난 기한이 아니다 */
const dueIso = d => (d ? T.zonedIso(d, 23 + 59 / 60) : null);   // 그날 23:59(앱 시간대)
const LINK_KIND = { mail: '메일', event: '일정', link: '링크' };

/** 참조 고르기 — 통합 검색(서버가 볼 수 있는 것만 준다)에서 결재·게시글을 고른다 */
function RefPicker({ type, label, value, onChange }) {
  const [q, setQ] = uS('');
  const [hits, setHits] = uS([]);
  uE(() => {
    const k = q.trim();
    if (!k) { setHits([]); return undefined; }
    let current = true;
    const timer = setTimeout(() => api.get('/api/v1/search?q=' + encodeURIComponent(k))
      .then(r => { if (current) setHits((r.groups.find(g => g.type === type)?.items ?? []).slice(0, 5)); }).catch(() => {}), 250);
    return () => { current = false; clearTimeout(timer); };
  }, [q, type]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} data-ref-picker={type}>
      <Lbl>{label}</Lbl>
      {value ? <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', fontSize: 12.5 }}>{value.title}
        <button type="button" className="link" onClick={() => onChange(null)}>빼기</button></span> : (
        <div style={{ position: 'relative' }}>
          <input style={{ ...box, width: '100%' }} value={q} onChange={e => setQ(e.target.value)} placeholder="제목으로 찾기" aria-label={`${label} 찾기`} />
          {hits.length > 0 && <div className="panel" style={{ position: 'absolute', left: 0, right: 0, top: 'calc(100% + 4px)', zIndex: 5, padding: 4 }}>
            {hits.map(h => <button key={h.id} type="button" onClick={() => { onChange({ id: h.id, title: h.title }); setQ(''); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 8px', fontSize: 12.5 }}>{h.title}<span style={{ color: 'var(--ink-3)' }}> · {h.sub}</span></button>)}
          </div>}
        </div>)}
    </div>
  );
}

/** 업무 쓰기·고치기 창 — 내용(editContent)이 없으면 진행만 */
function TaskEdit({ task, onClose, onSaved, onToast }) {
  const editing = Boolean(task);
  const content = !editing || task.actions.editContent;
  const progressOk = !editing || task.actions.editProgress;
  const [title, setTitle] = uS(task?.title ?? '');
  const [body, setBody] = uS(task?.body ?? '');
  const [assignee, setAssignee] = uS(task?.assignee_user_id ? [task.assignee_user_id] : []);
  const [watchers, setWatchers] = uS((task?.watchers ?? []).map(w => w.id));
  const [due, setDue] = uS(ymd(task?.due_at));
  const [priority, setPriority] = uS(task?.priority ?? 'mid');
  const [tag, setTag] = uS(task?.tag ?? '');
  const [status, setStatus] = uS(task?.status ?? 'todo');
  const [progress, setProgress] = uS(task?.progress ?? 0);
  const [approval, setApproval] = uS(task?.approval?.visible ? { id: task.approval.id, title: task.approval.title } : null);
  const [post, setPost] = uS(task?.post?.visible ? { id: task.post.id, title: task.post.title } : null);
  const [links, setLinks] = uS((task?.links ?? []).map(l => ({ kind: l.kind, url: l.url, title: l.title })));
  const [fileIds, setFileIds] = uS((task?.files ?? []).map(f => f.id));
  const [busy, setBusy] = uS(false);
  // 볼 수 없는 참조(다른 사람이 넣은 것)는 화면이 모른다 — 건드리지 않았으면 보내지 않는다
  const approvalHidden = task?.approval && !task.approval.visible, postHidden = task?.post && !task.post.visible;
  const save = () => {
    setBusy(true);
    const c = content ? { title: title.trim(), body: body.trim() || null, assigneeId: assignee[0] ?? undefined, watcherIds: watchers,
      dueAt: dueIso(due), priority, tag: tag.trim() || null, links,
      ...(approvalHidden ? {} : { approvalDocumentId: approval?.id ?? null }), ...(postHidden ? {} : { postId: post?.id ?? null }) } : {};
    const p = progressOk ? { status, progress: Number(progress), fileIds } : {};
    if (!editing) delete p.fileIds;   // 첨부는 등록한 뒤 고치기에서(업무가 있어야 붙는다)
    (editing ? api.patch('/api/v1/tasks/' + task.id, { ...c, ...p }) : api.post('/api/v1/tasks', { ...c, ...p }))
      .then(r => { onToast(editing ? '업무를 고쳤습니다' : '업무를 등록했습니다'); onSaved(r.id ?? task.id); })
      .catch(e => onToast(e.message))
      .finally(() => setBusy(false));
  };
  return (
    <Modal width={560} title={editing ? '업무 고치기' : '업무 등록'} icon="check-square" onClose={onClose}
      footer={<React.Fragment>
        <Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>
        <Button variant="primary" size="sm" disabled={busy || (content && !title.trim())} onClick={save}>{editing ? '저장' : '등록'}</Button>
      </React.Fragment>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }} data-task-edit>
        {content ? <React.Fragment>
          <Field label="제목" value={title} onChange={e => setTitle(e.target.value)} maxLength={200} />
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>내용</Lbl>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={5} maxLength={20000} aria-label="내용" style={{ ...box, resize: 'vertical' }} /></label>
          <PeoplePicker label="담당자 (비우면 나)" value={assignee} onChange={v => setAssignee(v.slice(-1))} max={1} includeMe />
          <PeoplePicker label="참조자 (보기·댓글)" value={watchers} onChange={setWatchers} max={50} />
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>기한</Lbl>
              <input type="date" style={box} value={due} onChange={e => setDue(e.target.value)} aria-label="기한" /></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>우선순위</Lbl>
              <select style={box} value={priority} onChange={e => setPriority(e.target.value)} aria-label="우선순위">
                {Object.entries(PRIO).map(([v, [l]]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <Field label="분류 (선택)" value={tag} onChange={e => setTag(e.target.value)} maxLength={40} style={{ flex: '1 1 140px' }} />
          </div>
          {editing && <React.Fragment>
            {approvalHidden ? <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>참조 결재: 볼 수 없는 문서(그대로 둡니다)</div>
              : <RefPicker type="approval" label="참조 결재(상신된 문서)" value={approval} onChange={setApproval} />}
            {postHidden ? <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>참조 게시글: 볼 수 없는 글(그대로 둡니다)</div>
              : <RefPicker type="post" label="참조 게시글" value={post} onChange={setPost} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }} data-task-links-edit>
              <Lbl>링크 (메일·일정 — https 주소)</Lbl>
              {links.map((l, i) => (
                <div key={i} style={{ display: 'flex', gap: 6 }}>
                  <select style={box} value={l.kind} aria-label={`링크 ${i + 1} 종류`} onChange={e => setLinks(x => x.map((y, j) => (j === i ? { ...y, kind: e.target.value } : y)))}>
                    {Object.entries(LINK_KIND).map(([v, n]) => <option key={v} value={v}>{n}</option>)}
                  </select>
                  <input style={{ ...box, flex: 1 }} value={l.title} placeholder="제목" maxLength={200} aria-label={`링크 ${i + 1} 제목`} onChange={e => setLinks(x => x.map((y, j) => (j === i ? { ...y, title: e.target.value } : y)))} />
                  <input style={{ ...box, flex: 2 }} value={l.url} placeholder="https://" maxLength={500} aria-label={`링크 ${i + 1} 주소`} onChange={e => setLinks(x => x.map((y, j) => (j === i ? { ...y, url: e.target.value } : y)))} />
                  <button type="button" className="link" onClick={() => setLinks(x => x.filter((_, j) => j !== i))}>빼기</button>
                </div>))}
              {links.length < 20 && <button type="button" className="link" style={{ alignSelf: 'flex-start' }} onClick={() => setLinks(x => [...x, { kind: 'mail', url: '', title: '' }])}>링크 더하기</button>}
            </div>
          </React.Fragment>}
        </React.Fragment> : <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>담당자는 진행(상태·진행률)과 첨부를 바꿉니다 — 내용은 등록자가 바꿉니다.</div>}
        {editing && progressOk && <Attachments scope="boards" canUpload initialFiles={task.files ?? []} onChange={list => setFileIds(list.map(f => f.id))} />}
        {progressOk && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>상태</Lbl>
              <select style={box} value={status} onChange={e => setStatus(e.target.value)} aria-label="상태">
                {STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}><Lbl>진행률 {status === 'done' ? 100 : progress}%</Lbl>
              <input type="range" min="0" max="100" step="10" value={status === 'done' ? 100 : progress} disabled={status === 'done'}
                onChange={e => setProgress(Number(e.target.value))} aria-label="진행률" style={{ width: 180 }} /></label>
          </div>
        )}
      </div>
    </Modal>
  );
}

/** 업무 상세 — 볼 수 없으면 서버가 404(알리고 닫는다) */
function TaskDetail({ id, onClose, onChanged, onToast }) {
  const [t, setT] = uS(null);
  const [editing, setEditing] = uS(false);
  const [confirmDelete, setConfirmDelete] = uS(false);
  const outer = React.useRef({ onToast, onClose });
  outer.current = { onToast, onClose };
  const load = uCb(() => Promise.all([api.get('/api/v1/tasks/' + id), api.get('/api/v1/files?taskId=' + id).catch(() => ({ files: [] }))])
    .then(([r, f]) => setT({ ...r.task, files: f.files }))
    .catch(e => { outer.current.onToast(e.message); outer.current.onClose(); }), [id]);
  const [comment, setComment] = uS('');
  const sendComment = () => api.post(`/api/v1/tasks/${id}/comments`, { body: comment.trim() })
    .then(() => { setComment(''); return load(); }).catch(e => onToast(e.message));
  const delComment = cid => api.post(`/api/v1/tasks/${id}/comments/${cid}/delete`, {}).then(load).catch(e => onToast(e.message));
  uE(() => { void load(); }, [load]);
  const remove = () => api.post(`/api/v1/tasks/${id}/delete`, {})
    .then(() => { onToast('업무를 지웠습니다'); onChanged(); onClose(); })
    .catch(e => onToast(e.message));
  const a = t?.actions ?? {};
  return (
    <React.Fragment>
      <Modal width={560} title="업무" icon="check-square" onClose={onClose}
        footer={t && <React.Fragment>
          <Button variant="secondary" size="sm" onClick={onClose}>닫기</Button>
          {confirmDelete ? <React.Fragment>
            <Button variant="secondary" size="sm" onClick={() => setConfirmDelete(false)}>돌아가기</Button>
            <Button variant="danger" size="sm" onClick={remove}>지우기 확인</Button>
          </React.Fragment> : <React.Fragment>
            {a.delete && <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>지우기</Button>}
            {(a.editContent || a.editProgress) && <Button variant="primary" size="sm" onClick={() => setEditing(true)}>{a.editContent ? '고치기' : '진행 바꾸기'}</Button>}
          </React.Fragment>}
        </React.Fragment>}>
        {!t ? <Loading title="불러오는 중" /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }} data-task-detail={t.id} data-status={t.status}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <StatusPill s={t.status} /><Pill tone={PRIO[t.priority][1]} dot>{PRIO[t.priority][0]}</Pill>
              {t.tag && <Pill tone="neutral" dot={false}>{t.tag}</Pill>}
              <b style={{ fontSize: 15 }}>{t.title}</b>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '72px 1fr', rowGap: 6, columnGap: 10 }}>
              <Lbl>요청</Lbl><span>{t.requester_name}{t.department_name ? ` · ${t.department_name}` : ''}</span>
              <Lbl>담당</Lbl><span>{t.assignee_name ?? '—'}</span>
              <Lbl>참조</Lbl><span data-task-watchers>{t.watchers.length ? t.watchers.map(w => w.name).join(', ') : '없음'}</span>
              <Lbl>기한</Lbl><span className="mono">{t.due_at ? T.ymd(t.due_at) : '—'}</span>
              <Lbl>진행</Lbl><span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><DataBar pct={t.progress} tone={t.status === 'done' ? 'ok' : 'accent'} width={90} />
                <span className="mono">{t.progress}%</span></span>
            </div>
            {t.body && <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.75 }}>{t.body}</p>}
            {(t.approval || t.post || t.links.length > 0) && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }} data-task-refs>
                <Lbl>참조</Lbl>
                {t.approval && (t.approval.visible ? <a href={`#/approval/${t.approval.id}`} data-ref="approval">결재 · {t.approval.doc_no} {t.approval.title}</a>
                  : <span style={{ color: 'var(--ink-3)' }} data-ref="approval-hidden">결재 · 볼 수 없는 문서</span>)}
                {t.post && (t.post.visible ? <a href={`#/board/${t.post.id}`} data-ref="post">게시글 · {t.post.title}</a>
                  : <span style={{ color: 'var(--ink-3)' }} data-ref="post-hidden">게시글 · 볼 수 없는 글</span>)}
                {t.links.map(l => <a key={l.id} href={l.url} target="_blank" rel="noopener noreferrer" data-ref="link">{LINK_KIND[l.kind]} · {l.title}</a>)}
              </div>)}
            <Attachments target={{ taskId: t.id }} label="첨부" />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-task-comments={t.comments.length}>
              <Lbl>댓글 {t.comments.length ? t.comments.length : ''}</Lbl>
              {t.comments.map(c => (
                <div key={c.id} style={{ fontSize: 12.5, borderLeft: '2px solid var(--line)', paddingLeft: 9 }} data-comment={c.id}>
                  <span style={{ fontWeight: 600 }}>{c.author_name}</span> <span className="mono" style={{ fontSize: 10.5, color: 'var(--ink-3)' }}>{T.dateTime(c.created_at)}</span>
                  {c.can_delete && <button className="link" style={{ marginLeft: 8, fontSize: 11 }} onClick={() => delComment(c.id)}>지우기</button>}
                  <div style={{ whiteSpace: 'pre-wrap', color: c.body === null ? 'var(--ink-3)' : 'var(--ink)' }}>{c.body ?? '지운 댓글입니다'}</div>
                </div>))}
              {a.comment && <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...box, flex: 1 }} value={comment} maxLength={2000} onChange={e => setComment(e.target.value)} placeholder="댓글" aria-label="댓글" />
                <Button variant="secondary" size="sm" disabled={!comment.trim()} onClick={sendComment}>쓰기</Button>
              </div>}
            </div>
          </div>
        )}
      </Modal>
      {editing && t && <TaskEdit task={t} onToast={onToast} onClose={() => setEditing(false)}
        onSaved={() => { setEditing(false); onChanged(); void load(); }} />}
    </React.Fragment>
  );
}

/**
 * 칸반 — 상태 네 열. 진행을 바꿀 수 있는 카드(서버 actions.update)만 옮긴다 — 끌어 놓기와 같은 일을 하는 '←·→' 단추(키보드로도).
 * 옮기면 서버가 상태를 바꾸고 알린다(2-5a 규칙 그대로)
 */
function Kanban({ list, onOpen, onMove }) {
  const [drag, setDrag] = uS(null);
  const order = STATUS.map(([v]) => v);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(180px, 1fr))', gap: 10, padding: 12, overflowX: 'auto' }} data-kanban>
      {STATUS.map(([st, label], col) => {
        const cards = list.filter(t => t.status === st);
        return (
          <div key={st} data-column={st} onDragOver={e => { if (drag && drag.status !== st) e.preventDefault(); }}
            onDrop={e => { e.preventDefault(); if (drag && drag.status !== st) onMove(drag, st); setDrag(null); }}
            style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)', padding: 8, minHeight: 160, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>{label} <span className="mono" style={{ color: 'var(--ink-3)' }}>{cards.length}</span></div>
            {cards.map(t => (
              <div key={t.id} draggable={t.canUpdate} onDragStart={() => setDrag(t)} onDragEnd={() => setDrag(null)} data-card={t.id}
                style={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '7px 8px', fontSize: 12.5, cursor: t.canUpdate ? 'grab' : 'default' }}>
                <button className="link" style={{ textAlign: 'left', color: 'var(--ink)', fontWeight: 600 }} onClick={() => onOpen(t.id)}>{t.title}</button>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)', flex: 1 }}>{t.assignee}{t.due ? ` · ${t.due}` : ''}</span>
                  {t.canUpdate && col > 0 && <button className="ico" style={{ width: 22, height: 22 }} aria-label={`${t.title} ${STATUS[col - 1][1]}(으)로`} onClick={() => onMove(t, order[col - 1])}><Icon name="chevron-left" size={12} /></button>}
                  {t.canUpdate && col < 3 && <button className="ico" style={{ width: 22, height: 22 }} aria-label={`${t.title} ${STATUS[col + 1][1]}(으)로`} onClick={() => onMove(t, order[col + 1])}><Icon name="chevron-right" size={12} /></button>}
                </div>
              </div>))}
          </div>);
      })}
    </div>
  );
}

function TaskView({ onToast, deepTask }) {
  // 지표(내 담당 요약)와 완료 체크 규칙은 홈과 같은 use-tasks — 목록은 탭(범위)마다 서버에서
  const { summary, reload: reloadSummary } = useTasks(onToast);
  const [scope, setScope] = uS('assigned');
  const [scopes, setScopes] = uS(['assigned', 'requested', 'watching']);
  const [filter, setFilter] = uS('all');
  const [mode, setMode] = uS('list');   // 목록 | 칸반
  const [list, setList] = uS(null);
  const [rev, setRev] = uS(0);
  const [detail, setDetail] = uS(null);
  const [creating, setCreating] = uS(false);
  uE(() => {
    let current = true;
    setList(null);
    api.get('/api/v1/tasks?scope=' + scope)
      .then(r => { if (current) { setList(mapTasks(r.tasks)); setScopes(r.scopes.filter(s => s !== 'mine')); } })
      .catch(e => { if (current) { setList([]); onToast(e.message); } });
    return () => { current = false; };
  }, [scope, rev, onToast]);
  uE(() => { if (deepTask?.id) setDetail(deepTask.id); }, [deepTask]);
  const changed = () => { setRev(x => x + 1); void reloadSummary?.(); };
  // 완료 체크(P1-4 규칙 그대로): 체크하면 완료, 같은 화면에서 풀면 체크하기 전에 서버가 줬던 상태·진행률로(처음부터 완료였던 업무는 '진행').
  // 저장이 실패하면 서버 상태 그대로 두고 이유를 알린다
  const before = React.useRef({});
  const toggle = t => {
    let body;
    if (t.status !== 'done') { before.current[t.id] = { status: t.status, progress: t.progress }; body = { status: 'done' }; }
    else { const orig = before.current[t.id]; body = orig ? { status: orig.status, progress: orig.progress } : { status: 'doing' }; }
    api.patch(`/api/v1/tasks/${t.id}`, body)
      .then(() => { if (t.status === 'done') delete before.current[t.id]; changed(); })
      .catch(e => { if (t.status !== 'done') delete before.current[t.id]; onToast(e.message); });
  };
  const rows = (list ?? []).filter(t => filter === 'all' || t.status === filter);
  const move = (t, status) => api.patch(`/api/v1/tasks/${t.id}`, { status }).then(changed).catch(e => onToast(e.message));
  const F = [{ value: 'all', label: '전체' }, { value: 'todo', label: '등록' }, { value: 'doing', label: '진행' }, { value: 'hold', label: '보류' }, { value: 'done', label: '완료' }];
  return (
    <React.Fragment>
      <div className="head">
        <h1>내 업무</h1>
        <div className="r"><Button variant="primary" size="sm" onClick={() => setCreating(true)}>업무 등록</Button></div>
      </div>
      <PageTabs items={scopes.map(s => ({ value: s, label: SCOPE_LABEL[s] }))} value={scope} onChange={setScope} />
      <div className="body" style={{ paddingTop: 14 }}>
        <div className="kpis">
          <Metric label="진행 중" value={summary.doing} unit="건" />
          <Metric label="오늘 마감" value={summary.due_today} unit="건" tone="down" delta={summary.overdue ? '지난 기한 ' + summary.overdue + '건' : undefined} />
          <Metric label="보류" value={summary.hold} unit="건" />
          <Metric label="이번 주 완료" value={summary.week_done} unit={'/ ' + summary.week_total} tone="up" />
        </div>
        <Panel scroll>
          <PH title={SCOPE_LABEL[scope]} sub={list ? (mode === 'board' ? list.length : rows.length) + '건' : ''} right={<span style={{ display: 'inline-flex', gap: 8 }}>
            <SegmentedControl size="sm" value={mode} options={[{ value: 'list', label: '목록' }, { value: 'board', label: '칸반' }]} onChange={setMode} />
            {mode === 'list' && <SegmentedControl size="sm" value={filter} options={F} onChange={setFilter} />}</span>} />
          {!list ? <Loading title="불러오는 중" /> : mode === 'board' ? <Kanban list={list} onOpen={setDetail} onMove={move} />
            : !rows.length ? <Empty icon="check-square" title="업무가 없습니다" /> : (
            <div data-task-list={scope}>
              <DataTable columns={[
                { key: 'chk', header: '', width: 34, render: t => (
                  <button onClick={() => toggle(t)} disabled={!t.canUpdate} aria-label="완료 토글" data-task={t.id} style={{
                    width: 16, height: 16, borderRadius: 'var(--r-sm)', display: 'grid', placeItems: 'center',
                    border: '1px solid ' + (t.status === 'done' ? 'var(--ok)' : 'var(--line-strong)'), background: t.status === 'done' ? 'var(--ok)' : 'transparent' }}>
                    {t.status === 'done' && <Icon name="check" size={10} color="var(--n-1000)" sw={2.6} />}
                  </button>) },
                { key: 'title', header: '업무', strong: true, render: t => (
                  <button className="link" onClick={() => setDetail(t.id)} data-task-row={t.id}
                    style={{ textAlign: 'left', textDecoration: t.status === 'done' ? 'line-through' : 'none', color: t.status === 'done' ? 'var(--ink-3)' : 'var(--ink)' }}>{t.title}</button>) },
                { key: 'people', header: scope === 'assigned' ? '요청' : '담당', width: 90, render: t => <span style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{scope === 'assigned' ? t.requester : t.assignee}</span> },
                { key: 'tag', header: '분류', width: 78, render: t => (t.tag ? <Pill tone="neutral" dot={false}>{t.tag}</Pill> : '') },
                { key: 'prog', header: '진행', align: 'right', width: 120, render: t => <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                  <DataBar pct={t.progress} tone={t.status === 'done' ? 'ok' : 'accent'} width={54} />
                  <span className="mono" style={{ fontSize: 11, color: 'var(--ink-3)', minWidth: 26, textAlign: 'right' }}>{t.progress}%</span></span> },
                { key: 'prio', header: '우선', width: 68, render: t => <Pill tone={PRIO[t.priority][1]} dot>{PRIO[t.priority][0]}</Pill> },
                { key: 'due', header: '기한', align: 'right', width: 74, render: t => (t.status === 'done' ? <Pill tone="ok" dot={false}>완료</Pill>
                  : t.due ? <Pill tone={t.due === '오늘' ? 'crit' : t.due === '내일' ? 'warn' : 'neutral'} dot={false}>{t.due}</Pill> : '') },
                { key: 'status', header: '상태', width: 68, render: t => <StatusPill s={t.status} /> },
              ]} rows={rows} />
            </div>
          )}
        </Panel>
      </div>
      {creating && <TaskEdit onToast={onToast} onClose={() => setCreating(false)} onSaved={id => { setCreating(false); changed(); setDetail(id); }} />}
      {detail && <TaskDetail id={detail} onToast={onToast} onClose={() => setDetail(null)} onChanged={changed} />}
    </React.Fragment>
  );
}

export { TaskView };
